import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, Plus, Search, Eye, Edit, Trash2, Upload, RefreshCw,
  CheckCircle, Clock, AlertCircle, Download, FileText, Copy,
  Mail, Phone, Calendar, Building2, Briefcase, X, ThumbsUp, ThumbsDown, BookOpen,
  GraduationCap, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminPreJoining = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUploadOfferModal, setShowUploadOfferModal] = useState(false);
  const [showMoveToTrainingConfirm, setShowMoveToTrainingConfirm] = useState(false);
  const [movingToTraining, setMovingToTraining] = useState(false);
  
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDocuments, setCandidateDocuments] = useState([]);
  const [policyAcceptances, setPolicyAcceptances] = useState([]);
  const [reviewingDoc, setReviewingDoc] = useState(null);
  const [reviewRemarks, setReviewRemarks] = useState("");
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    designation: "",
    department: "",
    joining_date: "",
    salary: "",
    notes: ""
  });
  
  const [offerFile, setOfferFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getToken = () => localStorage.getItem("admin_token");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pre-joining`, authHeaders());
      setCandidates(response.data);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateDocuments = async (candidateId) => {
    try {
      const response = await axios.get(`${API}/pre-joining/${candidateId}/documents`, authHeaders());
      setCandidateDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const fetchPolicyAcceptances = async (candidateId) => {
    try {
      const response = await axios.get(`${API}/pre-joining/${candidateId}/policy-acceptances`, authHeaders());
      setPolicyAcceptances(response.data);
    } catch (error) {
      console.error("Error fetching policy acceptances:", error);
      setPolicyAcceptances([]);
    }
  };

  const handleReviewDocument = async (docId, status) => {
    setReviewingDoc(docId);
    try {
      const data = new FormData();
      data.append("status", status);
      if (reviewRemarks) {
        data.append("remarks", reviewRemarks);
      }

      await axios.put(`${API}/pre-joining/${selectedCandidate.id}/documents/${docId}/review`, data, authHeaders());
      toast.success(`Document ${status}`);
      setReviewRemarks("");
      fetchCandidateDocuments(selectedCandidate.id);
    } catch (error) {
      toast.error("Failed to review document");
    } finally {
      setReviewingDoc(null);
    }
  };

  const handleAddCandidate = () => {
    setSelectedCandidate(null);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      designation: "",
      department: "",
      joining_date: "",
      salary: "",
      notes: ""
    });
    setShowAddModal(true);
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData({
      full_name: candidate.full_name,
      email: candidate.email,
      phone: candidate.phone,
      designation: candidate.designation,
      department: candidate.department,
      joining_date: candidate.joining_date,
      salary: candidate.salary || "",
      notes: candidate.notes || ""
    });
    setShowAddModal(true);
  };

  const handleViewCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    await Promise.all([
      fetchCandidateDocuments(candidate.id),
      fetchPolicyAcceptances(candidate.id)
    ]);
    setShowViewModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) data.append(key, formData[key]);
      });

      if (selectedCandidate) {
        // Update existing
        await axios.put(`${API}/pre-joining/${selectedCandidate.id}`, data, authHeaders());
        toast.success("Candidate updated successfully");
      } else {
        // Create new
        const response = await axios.post(`${API}/pre-joining`, data, authHeaders());
        toast.success(
          <div>
            <p>Candidate added successfully!</p>
            <p className="font-mono mt-2 bg-slate-100 p-2 rounded">
              Access Code: <strong>{response.data.candidate.access_code}</strong>
            </p>
          </div>
        );
      }

      setShowAddModal(false);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCandidate) return;
    
    try {
      await axios.delete(`${API}/pre-joining/${selectedCandidate.id}`, authHeaders());
      toast.success("Candidate deleted successfully");
      setShowDeleteConfirm(false);
      setSelectedCandidate(null);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const handleDownloadSignedOffer = async (candidate) => {
    try {
      const response = await axios.get(`${API}/pre-joining/${candidate.id}/signed-offer-letter`, authHeaders());
      const { data, filename, signed, signed_at, candidate_name } = response.data;
      
      if (!signed) {
        toast.warning("Offer letter has not been signed yet");
        return;
      }
      
      // Decode base64 and create download
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Signed_${candidate_name?.replace(/\s+/g, '_')}_${filename || 'Offer_Letter.pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded signed offer letter for ${candidate_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download signed offer letter");
    }
  };

  const handleUploadOffer = async () => {
    if (!offerFile || !selectedCandidate) return;
    
    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append("offer_letter", offerFile);

      await axios.post(`${API}/pre-joining/${selectedCandidate.id}/offer-letter`, data, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" }
      });

      toast.success("Offer letter uploaded successfully");
      setShowUploadOfferModal(false);
      setOfferFile(null);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to upload offer letter");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleMoveToTraining = async () => {
    if (!selectedCandidate) return;
    
    setMovingToTraining(true);
    try {
      const response = await axios.post(
        `${API}/pre-joining/${selectedCandidate.id}/move-to-training`,
        {},
        authHeaders()
      );
      
      toast.success(
        <div>
          <p className="font-medium">Candidate moved to Training Portal!</p>
          <p className="text-sm text-slate-600 mt-1">Access Code: <span className="font-mono font-bold">{response.data.access_code}</span></p>
          {response.data.email_sent && (
            <p className="text-sm text-green-600 mt-1">✓ Welcome email sent</p>
          )}
        </div>,
        { duration: 6000 }
      );
      
      setShowMoveToTrainingConfirm(false);
      setShowViewModal(false);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move candidate to training");
    } finally {
      setMovingToTraining(false);
    }
  };

  const handleRegenerateCode = async (candidateId) => {
    try {
      const response = await axios.post(`${API}/pre-joining/${candidateId}/regenerate-code`, {}, authHeaders());
      toast.success(
        <div>
          <p>New access code generated!</p>
          <p className="font-mono mt-2 bg-slate-100 p-2 rounded">
            <strong>{response.data.access_code}</strong>
          </p>
        </div>
      );
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to regenerate code");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadDocument = async (candidateId, docId, filename) => {
    try {
      const response = await axios.get(`${API}/pre-joining/${candidateId}/documents/${docId}/download`, authHeaders());
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${response.data.file_data}`;
      link.download = filename;
      link.click();
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      documents_uploaded: "bg-blue-100 text-blue-800",
      offer_signed: "bg-purple-100 text-purple-800",
      ready_to_join: "bg-green-100 text-green-800"
    };
    const labels = {
      pending: "Pending",
      documents_uploaded: "Docs Uploaded",
      offer_signed: "Offer Signed",
      ready_to_join: "Ready to Join"
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || "Pending"}</Badge>;
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.designation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: candidates.length,
    pending: candidates.filter(c => c.status === "pending").length,
    ready: candidates.filter(c => c.status === "ready_to_join").length,
    offerPending: candidates.filter(c => !c.offer_letter_uploaded).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pre-Joining Candidates</h1>
          <p className="text-slate-500">Manage candidates who are ready to join</p>
        </div>
        <Button onClick={handleAddCandidate} className="bg-[#1e3a8a] hover:bg-[#172554]" data-testid="admin-add-candidate-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Candidate
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Candidates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ready}</p>
                <p className="text-sm text-slate-500">Ready to Join</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.offerPending}</p>
                <p className="text-sm text-slate-500">Offer Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="documents_uploaded">Docs Uploaded</SelectItem>
            <SelectItem value="offer_signed">Offer Signed</SelectItem>
            <SelectItem value="ready_to_join">Ready to Join</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Candidates Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1e3a8a] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-slate-500 mt-4">Loading candidates...</p>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No candidates found</h3>
              <p className="text-slate-500 mt-1">Add your first pre-joining candidate to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Candidate</th>
                    <th className="text-left p-4 font-medium text-slate-600">Position</th>
                    <th className="text-left p-4 font-medium text-slate-600">Joining Date</th>
                    <th className="text-left p-4 font-medium text-slate-600">Access Code</th>
                    <th className="text-left p-4 font-medium text-slate-600">Status</th>
                    <th className="text-left p-4 font-medium text-slate-600">Offer</th>
                    <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCandidates.map((candidate) => (
                    <tr key={candidate.id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-slate-900">{candidate.full_name}</p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-slate-900">{candidate.designation}</p>
                        <p className="text-sm text-slate-500">{candidate.department}</p>
                      </td>
                      <td className="p-4 text-slate-900">{candidate.joining_date}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                            {candidate.access_code}
                          </code>
                          <button onClick={() => copyToClipboard(candidate.access_code)} className="text-slate-400 hover:text-slate-600">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(candidate.status)}</td>
                      <td className="p-4">
                        {candidate.offer_letter_uploaded ? (
                          <Badge className="bg-green-100 text-green-800">
                            {candidate.offer_signed ? "Signed" : "Uploaded"}
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Pending</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleViewCandidate(candidate)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditCandidate(candidate)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowUploadOfferModal(true);
                            }}
                            title="Upload Offer Letter"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                          {candidate.offer_signed && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleDownloadSignedOffer(candidate)}
                              title="Download Signed Offer Letter"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Move to Training - only if offer signed and not already moved */}
                          {candidate.offer_signed && !candidate.moved_to_training && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setShowMoveToTrainingConfirm(true);
                              }}
                              title="Move to Training Portal"
                            >
                              <GraduationCap className="w-4 h-4" />
                            </Button>
                          )}
                          {candidate.moved_to_training && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              In Training
                            </Badge>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowDeleteConfirm(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedCandidate ? "Edit Candidate" : "Add New Candidate"}</DialogTitle>
            <DialogDescription>
              {selectedCandidate 
                ? "Update the candidate's information" 
                : "Add a new pre-joining candidate. They will receive an access code to login."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                  data-testid="candidate-form-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={!!selectedCandidate}
                  data-testid="candidate-form-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="candidate-form-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Designation *</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  placeholder="e.g., SEO Specialist"
                  required
                  data-testid="candidate-form-designation"
                />
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="e.g., Digital Marketing"
                  required
                  data-testid="candidate-form-department"
                />
              </div>
              <div className="space-y-2">
                <Label>Joining Date *</Label>
                <Input
                  type="date"
                  value={formData.joining_date}
                  onChange={(e) => setFormData({...formData, joining_date: e.target.value})}
                  required
                  data-testid="candidate-form-joining-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Salary (Optional)</Label>
                <Input
                  value={formData.salary}
                  onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  placeholder="e.g., ₹5,00,000 per annum"
                  data-testid="candidate-form-salary"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes about the candidate..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#172554]" disabled={isSubmitting} data-testid="candidate-form-submit">
                {isSubmitting ? "Saving..." : (selectedCandidate ? "Update" : "Add Candidate")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Candidate Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate Details</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <>
            <Tabs defaultValue="info" className="space-y-4">
              <TabsList>
                <TabsTrigger value="info">Information</TabsTrigger>
                <TabsTrigger value="documents">Documents ({candidateDocuments.length})</TabsTrigger>
                <TabsTrigger value="policies">Policy Acceptances ({policyAcceptances.length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Full Name</p>
                    <p className="font-medium">{selectedCandidate.full_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{selectedCandidate.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{selectedCandidate.phone}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Designation</p>
                    <p className="font-medium">{selectedCandidate.designation}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Department</p>
                    <p className="font-medium">{selectedCandidate.department}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Joining Date</p>
                    <p className="font-medium">{selectedCandidate.joining_date}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Status</p>
                    {getStatusBadge(selectedCandidate.status)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Access Code</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-100 px-3 py-1 rounded font-mono text-lg">
                        {selectedCandidate.access_code}
                      </code>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(selectedCandidate.access_code)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRegenerateCode(selectedCandidate.id)}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Offer Letter Status</h4>
                  {selectedCandidate.offer_letter_uploaded ? (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">
                          {selectedCandidate.offer_signed ? "Offer Signed" : "Offer Uploaded"}
                        </span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        File: {selectedCandidate.offer_letter_filename}
                      </p>
                      {selectedCandidate.offer_signed && (
                        <p className="text-sm text-green-700">
                          Signed on: {new Date(selectedCandidate.offer_signed_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-800">Offer letter not uploaded</span>
                      </div>
                      <Button 
                        size="sm" 
                        className="mt-2 bg-[#1e3a8a]"
                        onClick={() => {
                          setShowViewModal(false);
                          setShowUploadOfferModal(true);
                        }}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Now
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="documents">
                {candidateDocuments.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {candidateDocuments.map((doc) => (
                      <div key={doc.id} className={`p-4 rounded-lg border-2 ${
                        doc.review_status === 'approved' ? 'border-green-200 bg-green-50' :
                        doc.review_status === 'rejected' ? 'border-red-200 bg-red-50' :
                        'border-slate-200 bg-slate-50'
                      }`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            {doc.review_status === 'approved' ? (
                              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                            ) : doc.review_status === 'rejected' ? (
                              <X className="w-5 h-5 text-red-600 mt-0.5" />
                            ) : (
                              <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium capitalize">{doc.document_type.replace(/_/g, " ")}</p>
                              <p className="text-sm text-slate-500">{doc.filename}</p>
                              {doc.review_status && (
                                <div className="mt-1">
                                  <Badge className={
                                    doc.review_status === 'approved' ? 'bg-green-100 text-green-800' :
                                    doc.review_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-amber-100 text-amber-800'
                                  }>
                                    {doc.review_status === 'approved' ? 'Approved' : 
                                     doc.review_status === 'rejected' ? 'Rejected' : 'Pending'}
                                  </Badge>
                                  {doc.reviewed_at && (
                                    <span className="text-xs text-slate-500 ml-2">
                                      {new Date(doc.reviewed_at).toLocaleString()}
                                    </span>
                                  )}
                                  {doc.review_remarks && (
                                    <p className="text-sm text-slate-600 mt-1">Remarks: {doc.review_remarks}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadDocument(selectedCandidate.id, doc.id, doc.filename)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                            {doc.review_status !== 'approved' && (
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 hover:bg-green-50"
                                  onClick={() => handleReviewDocument(doc.id, 'approved')}
                                  disabled={reviewingDoc === doc.id}
                                >
                                  <ThumbsUp className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const remarks = prompt("Enter rejection reason (optional):");
                                    setReviewRemarks(remarks || "");
                                    handleReviewDocument(doc.id, 'rejected');
                                  }}
                                  disabled={reviewingDoc === doc.id}
                                >
                                  <ThumbsDown className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="policies">
                {policyAcceptances.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No policies accepted yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {policyAcceptances.map((acceptance) => (
                      <div key={acceptance.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium">Policy ID: {acceptance.policy_id}</p>
                            <p className="text-sm text-green-700">
                              Accepted on: {new Date(acceptance.accepted_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            {/* Move to Training Section - Only show if offer is signed and not already moved */}
            {selectedCandidate?.offer_signed && !selectedCandidate?.moved_to_training && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-semibold text-green-800 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5" />
                        Ready for Training
                      </h4>
                      <p className="text-sm text-green-700 mt-1">
                        This candidate has signed their offer letter and is ready to start training.
                      </p>
                    </div>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => setShowMoveToTrainingConfirm(true)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Move to Training
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show if already moved to training */}
            {selectedCandidate?.moved_to_training && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Moved to Training Portal</span>
                  </div>
                  <p className="text-sm text-blue-700 mt-1">
                    This candidate has been moved to the training portal on {new Date(selectedCandidate.moved_to_training_at).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
        </DialogContent>
      </Dialog>

      {/* Upload Offer Modal */}
      <Dialog open={showUploadOfferModal} onOpenChange={setShowUploadOfferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Offer Letter</DialogTitle>
            <DialogDescription>
              Upload the offer letter PDF for {selectedCandidate?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setOfferFile(e.target.files[0])}
                className="hidden"
                id="offer-file"
              />
              <Label htmlFor="offer-file" className="cursor-pointer">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">
                  {offerFile ? offerFile.name : "Click to upload PDF"}
                </p>
                <p className="text-sm text-slate-400 mt-1">PDF files only</p>
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUploadOfferModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUploadOffer}
                disabled={!offerFile || isSubmitting}
                className="bg-[#1e3a8a] hover:bg-[#172554]"
              >
                {isSubmitting ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedCandidate?.full_name} and all their uploaded documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Move to Training Confirmation Modal */}
      <AlertDialog open={showMoveToTrainingConfirm} onOpenChange={setShowMoveToTrainingConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-green-600" />
              Move to Training Portal
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to move <strong>{selectedCandidate?.full_name}</strong> to the Training Portal?
              </p>
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><strong>What will happen:</strong></p>
                <ul className="text-sm space-y-1 list-disc list-inside text-slate-600">
                  <li>A new training account will be created</li>
                  <li>An access code will be generated</li>
                  <li>A welcome email with login credentials will be sent</li>
                  <li>The candidate can start their 3-day training program</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={movingToTraining}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMoveToTraining}
              disabled={movingToTraining}
              className="bg-green-600 hover:bg-green-700"
            >
              {movingToTraining ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Move to Training
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPreJoining;
